<script lang="ts">
  /**
   * The library: what the player has built, in the order the program reads it.
   *
   * This is a reference to the player's own vocabulary, not a second editor and
   * not a progress report. Two questions bring anyone here - "what can I use?"
   * and "where did this come from?" - and both are answered by showing the
   * definitions the way the files hold them: chapter by chapter, in the order
   * they were written, each name next to the task that asked for it.
   *
   * The order is the same order the level editor shows, so a reader can move
   * between the two without translating anything. Nothing is sorted by a
   * machine notion of dependency, because a name has to be defined before it is
   * used: what is written is already in the order it can be understood.
   */
  import { content, levelById } from '../content';
  import { game } from '../state/game.svelte';
  import Progress from './Progress.svelte';
  import ConfirmButton from './ConfirmButton.svelte';

  let filter = $state('');

  const library = $derived(game.wholeLibrary());
  const wanted = $derived(filter.trim().toLowerCase());
  const matches = $derived(
    wanted === ''
      ? library
      : library.filter((entry) => `${entry.name} ${entry.body}`.toLowerCase().includes(wanted)),
  );
  /** How many chapters have anything written in them, filter or no filter. */
  const written = $derived(new Set(library.map((entry) => entry.chapterId)).size);

  /**
   * The definitions grouped by the file that holds them, in reading order.
   *
   * A chapter with nothing matching the filter is left out, so a search shows
   * the few definitions it found rather than the shape of the whole library.
   */
  const files = $derived.by(() => {
    const grouped = new Map<string, typeof matches>();
    for (const entry of matches) {
      const existing = grouped.get(entry.chapterId);
      if (existing === undefined) {
        grouped.set(entry.chapterId, [entry]);
      } else {
        existing.push(entry);
      }
    }
    return content.chapters
      .filter((chapter) => grouped.has(chapter.id))
      .map((chapter) => {
        const progress = game.chapterProgress(chapter.id);
        const unfinished = chapter.levels.find((level) => game.statusOf(level.id) !== 'solved');
        return {
          id: chapter.id,
          title: chapter.title,
          entries: grouped.get(chapter.id) ?? [],
          solved: progress.solved,
          total: progress.total,
          // Where the player would carry on writing this file.
          nextId: (unfinished ?? chapter.levels[0])?.id ?? '',
        };
      });
  });

  /** How a definition's origin reads: the task that asked for it, or a helper. */
  function originFor(entry: { levelId: string; signature: string }): string {
    if (entry.levelId === '') {
      return 'a helper you wrote';
    }
    const level = levelById.get(entry.levelId);
    const title = level?.title ?? '';
    return entry.signature === ''
      ? `level ${entry.levelId} ${title}`
      : `level ${entry.levelId} ${title} · ${entry.signature}`;
  }
</script>

<article class="page library">
  <header>
    <h1>Library</h1>
    <p class="lede">
      Everything you have defined, in the order a program reads it:
      {library.length} definition{library.length === 1 ? '' : 's'} across {written} chapter{written === 1 ? '' : 's'}.
    </p>
  </header>

  {#if library.length === 0}
    <p class="empty">Nothing defined yet.</p>
  {:else}
    <div class="search">
      <input
        class="filter"
        type="text"
        placeholder="find a name or a body"
        bind:value={filter}
        aria-label="Find a definition"
      />
      {#if wanted !== ''}
        <span class="count" aria-live="polite">
          {matches.length} of {library.length}
        </span>
      {/if}
    </div>

    {#if files.length === 0}
      <p class="empty">Nothing matches `{filter}`.</p>
    {:else}
      {#each files as file (file.id)}
        <section>
          <div class="section-head">
            <span class="chapter">{file.title}</span>
            {#if file.solved === file.total}
              <span class="done">solved</span>
            {:else}
              <span class="count">{file.solved}/{file.total}</span>
            {/if}
            <span class="spacer"></span>
            {#if file.nextId !== ''}
              <button
                class="continue"
                title={`carry on with level ${file.nextId}, which writes in this file`}
                onclick={() => game.openLevel(file.nextId)}
              >
                write more →
              </button>
            {/if}
          </div>
          <ul class="definitions">
            {#each file.entries as entry (entry.name)}
              <li>
                <code><span class="name">{entry.name}</span> = {entry.body}</code>
                {#if entry.levelId === ''}
                  <span class="helper" title={originFor(entry)}>helper</span>
                {:else}
                  <button
                    class="owner"
                    title={originFor(entry)}
                    onclick={() => game.openLevel(entry.levelId)}
                  >
                    {entry.levelId}
                  </button>
                {/if}
              </li>
            {/each}
          </ul>
        </section>
      {/each}
    {/if}
  {/if}

  <footer>
    <div class="section-head">check</div>
    <div class="actions">
      <button disabled={game.progress !== null} onclick={() => void game.checkAll()}>
        Check every level
      </button>
      <ConfirmButton
        label="Erase everything"
        confirmLabel="erase everything?"
        title="Erases your programs too"
        onconfirm={() => void game.resetEverything()}
      />
      <span class="run"><Progress /></span>
      {#if !game.progress}
        {#if game.notice}
          <span class="notice" class:failed={game.noticeFailed} aria-live="polite">{game.notice}</span>
        {:else}
          <span class="version">engine {game.engineVersion || '…'}</span>
        {/if}
      {/if}
    </div>
  </footer>
</article>

<style>
  h1 {
    margin: 0 0 6px;
  }

  .lede {
    margin: 0 0 10px;
  }

  .empty {
    color: var(--text-faint);
    font-size: 13px;
  }

  .search {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 16px 0 4px;
  }

  .filter {
    flex: 1;
  }

  .search .count {
    flex: none;
    font-family: var(--mono);
    font-size: 12px;
    color: var(--text-faint);
  }

  section {
    margin-top: 18px;
  }

  .section-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--text-faint);
    padding: 4px 2px 6px;
    border-bottom: 1px solid var(--border);
  }

  .section-head .chapter {
    color: var(--text-dim);
  }

  .section-head .done {
    color: var(--pass);
  }

  .count {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0;
  }

  .spacer {
    flex: 1;
  }

  .continue {
    padding: 1px 8px;
    font-size: 11px;
    text-transform: none;
    letter-spacing: 0;
    background: none;
    color: var(--text-faint);
  }

  .continue:hover {
    color: var(--accent);
    border-color: var(--accent);
  }

  .definitions {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 2px;
  }

  .definitions li {
    display: flex;
    align-items: baseline;
    gap: 10px;
  }

  .definitions code {
    flex: 1;
    background: none;
    color: var(--text-dim);
    padding: 2px 0;
    overflow-wrap: anywhere;
  }

  .definitions .name {
    color: var(--text);
  }

  .owner {
    flex: none;
    padding: 1px 7px;
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text-faint);
    background: none;
  }

  .owner:hover {
    color: var(--accent);
    border-color: var(--accent);
  }

  .helper {
    flex: none;
    font-family: var(--mono);
    font-size: 11px;
    color: var(--text-faint);
    opacity: 0.75;
  }

  footer {
    margin-top: 28px;
  }

  footer .section-head {
    border-bottom: none;
    padding-bottom: 0;
  }

  .actions {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  /* The progress row sits at the far end of the actions. */
  .run {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .notice,
  .version {
    color: var(--text-faint);
    font-size: 12px;
  }

  .notice.failed {
    color: var(--fail);
  }
</style>
